/**
 * Typed endpoints for /coach/admin/clients/:id/cohorts.
 *
 * Transport, error class and status mapping come from `src/api/client.ts`
 * (ADR-0056). This module was the seventh hand-rolled client and the one
 * that motivated the ADR: it had to independently discover that a Pydantic
 * 422 returns `detail` as an array, a fix the other seven never got. That
 * knowledge now lives in the transport.
 *
 * Every route is nested under the client. That is a server-side
 * decision worth knowing here: it means a cohort id from another client
 * 404s by construction rather than by a check someone has to remember.
 */
import { AdminApiError, request } from '../api/client.js'
import type {
  CohortDetail,
  CohortHrAccess,
  CohortListResponse,
  CreateCohortBody,
  ListCohortsParams,
  UpdateCohortBody,
} from './types.js'

function base(clientId: string): string {
  return `/coach/admin/clients/${clientId}/cohorts`
}

export const adminCohortClient = {
  /**
   * GLOBAL list — every client, soonest start first by default, the client as a
   * filterable column. This is the cohort navigation entrypoint; the
   * per-client `list` below is what the client detail page needs.
   */
  listAll(params: ListCohortsParams = {}): Promise<CohortListResponse> {
    const qs = new URLSearchParams()
    if (params.clientId) qs.set('clientId', params.clientId)
    for (const cohortId of params.cohortIds ?? []) {
      qs.append('cohortId', cohortId)
    }
    if (params.search) qs.set('search', params.search)
    if (params.sortField) qs.set('sortField', params.sortField)
    if (params.sortDirection) qs.set('sortDirection', params.sortDirection)
    if (params.startingBefore) qs.set('startingBefore', params.startingBefore)
    if (params.endingBefore) qs.set('endingBefore', params.endingBefore)
    if (params.endingAfter) qs.set('endingAfter', params.endingAfter)
    if (params.startingAfter) qs.set('startingAfter', params.startingAfter)
    if (params.isTrial !== undefined) qs.set('isTrial', String(params.isTrial))
    // Explicit undefined check: `false` ("only ended cohorts") is a real
    // value, so a truthiness test would silently drop it.
    if (params.joinable !== undefined) qs.set('joinable', String(params.joinable))
    if (params.limit !== undefined) qs.set('limit', String(params.limit))
    if (params.offset !== undefined) qs.set('offset', String(params.offset))
    const suffix = qs.size > 0 ? `?${qs.toString()}` : ''
    return request<CohortListResponse>(`/coach/admin/cohorts${suffix}`, {
      method: 'GET',
    })
  },
  /** Every live cohort in one client, soonest start first. Unpaged. */
  list(clientId: string): Promise<CohortListResponse> {
    return request<CohortListResponse>(base(clientId), { method: 'GET' })
  },
  get(clientId: string, cohortId: string): Promise<CohortDetail> {
    return request<CohortDetail>(`${base(clientId)}/${cohortId}`, {
      method: 'GET',
    })
  },
  create(clientId: string, body: CreateCohortBody): Promise<CohortDetail> {
    return request<CohortDetail>(base(clientId), {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
  update(
    clientId: string,
    cohortId: string,
    patch: UpdateCohortBody,
  ): Promise<CohortDetail> {
    return request<CohortDetail>(`${base(clientId)}/${cohortId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    })
  },
  /** Soft delete. 409 `cohort_has_members` while any member remains. */
  remove(clientId: string, cohortId: string): Promise<void> {
    return request<void>(`${base(clientId)}/${cohortId}`, { method: 'DELETE' })
  },
  getHrAccess(clientId: string, cohortId: string): Promise<CohortHrAccess> {
    return request<CohortHrAccess>(`${base(clientId)}/${cohortId}/hr-access`, {
      method: 'GET',
    })
  },
  setHrAccess(
    clientId: string,
    cohortId: string,
    hrUserIds: string[],
  ): Promise<CohortHrAccess> {
    return request<CohortHrAccess>(`${base(clientId)}/${cohortId}/hr-access`, {
      method: 'PUT',
      body: JSON.stringify({ hrUserIds }),
    })
  },
}

/**
 * Which FIELD each server error belongs to, so the message renders at that
 * field instead of in a summary below the submit button.
 *
 * `invite_send_after_access_end` maps to the SEND fields rather than the
 * access end: the send is what the operator was editing, and the message
 * already names the end date they need to compare against.
 */
export const COHORT_ERROR_FIELD = {
  cohort_name_exists: 'name',
  access_ends_on_required: 'accessEndsOn',
  access_ends_before_start: 'accessEndsOn',
  offset_needs_scheduled_start: 'send',
  invite_send_after_access_end: 'send',
  program_not_found: 'defaultProgramId',
  program_unavailable: 'defaultProgramId',
  program_not_ready: 'defaultProgramId',
} as const

export type CohortField =
  (typeof COHORT_ERROR_FIELD)[keyof typeof COHORT_ERROR_FIELD]

/** Map the server's `detail` code to an operator-readable message. */
export function messageForCohortError(err: unknown): string {
  if (err instanceof AdminApiError) {
    switch (err.detail) {
      case 'cohort_name_exists':
        return 'This client already has a cohort with that name. (Other clients can reuse it.)'
      case 'cohort_has_members':
        return 'This cohort has enrollment history and cannot be deleted. Keep it as the reporting record for those periods.'
      case 'cohort_trial_locked':
        return 'Trial classification is locked because a student has already been added to this cohort.'
      case 'hr_all_cohort_access_locked':
        return 'An HR user with all-cohort access cannot be removed from a cohort. Turn it off on their page first.'
      case 'cohort_not_found':
        return 'That cohort no longer exists.'
      case 'cohort_access_ended':
        return "That cohort's access period has already ended. Move the end date first, or pick a different cohort."
      case 'access_ends_on_required':
        return 'An end date is required. For open-ended access, set a far-future date deliberately.'
      case 'scheduled_start_on_required':
        return 'A cohort needs a start date for future enrollments. Existing enrollment periods keep their locked dates.'
      case 'offset_needs_scheduled_start':
        return 'A relative send date needs a start date to be relative to. Set a start date, or switch to a fixed send date.'
      case 'access_ends_before_start':
        return 'The end date is before the start date — the cohort would end before it begins.'
      case 'invite_send_after_access_end':
        return 'The invite would go out after access has already ended, so it would arrive as a dead link. Move the send date, or extend the end date.'
      case 'program_not_found':
      case 'program_not_ready':
        return 'This program is not ready. Check its diagnostic, default tutor and lessons at every supported level.'
      case 'program_unavailable':
        return 'Choose an active program.'
      case 'admin_required':
        return 'You do not have permission to manage cohorts.'
      default:
        return err.message
    }
  }
  return err instanceof Error ? err.message : 'Request failed'
}
